import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LogoFull, LogoMark } from "../Logo";
import { WHATSAPP_URL } from "../../lib/contact";
import { Button } from "../ui/button";
import { useI18n } from "../../lib/i18n";
import LanguageToggle from "./LanguageToggle";

export default function Nav() {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const LINKS = [
    { href: "#servicios",  label: t("nav.services"),  key: "services" },
    { href: "#portafolio", label: t("nav.portfolio"), key: "portfolio" },
    { href: "#nosotros",   label: t("nav.about"),     key: "about" },
    { href: "#contacto",   label: t("nav.contact"),   key: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-md bg-background/75 border-b border-line"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center" data-testid="nav-logo">
          <LogoFull className="hidden sm:block h-9 md:h-10 w-auto" />
          <LogoMark className="sm:hidden h-8 w-auto" />
        </Link>

        {/* Center links */}
        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.key}
              href={l.href}
              data-testid={`nav-link-${l.key}`}
              className="text-sm font-medium text-secondary-fg hover:text-ember transition-colors tracking-wide"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Right: lang toggle + CTA + mobile toggle */}
        <div className="flex items-center gap-2 md:gap-3">
          <LanguageToggle />

          <Button
            asChild
            data-testid="nav-cta"
            className="hidden md:inline-flex bg-ember hover:bg-ember/90 text-white border-0 rounded-md font-medium tracking-wide h-9 px-4"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">{t("nav.cta")}</a>
          </Button>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden w-11 h-11 inline-flex items-center justify-center rounded-md border border-line"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Open menu"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-line bg-background/95 backdrop-blur"
        >
          <div className="container-x py-5 flex flex-col gap-4">
            {LINKS.map((l) => (
              <a
                key={l.key}
                href={l.href}
                className="inline-flex items-center min-h-11 text-base text-primary-fg/90 hover:text-ember"
                onClick={() => setMobileOpen(false)}
              >
                {l.label}
              </a>
            ))}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileOpen(false)}
              className="mt-2 inline-flex items-center justify-center min-h-11 h-11 rounded-md bg-ember text-white font-medium"
            >
              {t("nav.cta")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
